import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ButtonBase, IconButton, Typography, useTheme } from '@mui/material';
import { useSetAtom } from 'jotai';
import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useMatch } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { SourcingList } from '../../../../../schemas/SourcingList.schema';
import { useToastMessageState } from '../../../../ToastMessage/ToastMessageProvider';
import { useContextMenu } from '../../../../ContextMenu/useContextMenu';
import { SourcingMenu } from '../../../../../pages/Sourcing/components/SourcingMenu';
import { buildListMenuSections } from '../../../../../pages/Sourcing/util/buildListMenuSections';
import {
  useDuplicateListDialog,
  useEnrichListNow,
} from '../../../../../pages/Sourcing/hooks/useSourcingListActions';
import { DuplicateListDialog } from '../../../../../pages/Sourcing/DuplicateListDialog';
import { refreshSettingsDialogAtom } from '../../../../../pages/Sourcing/state/SourcingListsState';
import {
  ItemInner,
  ItemLeft,
  ItemRow,
  ListCompanyIcon,
  ListPersonIcon,
  NewItemInputWrapper,
  StyledInputBase,
} from './SourcingNavItemShared';

export interface SourcingListItemProps {
  item: SourcingList;
  onDelete: (item: SourcingList) => void;
  onRename: (item: SourcingList, newName: string) => void;
}

export const SourcingListItem: FC<SourcingListItemProps> = ({ item, onDelete, onRename }) => {
  const { secondary } = useTheme().colors;
  const { pushErrorToast } = useToastMessageState();
  const match = useMatch(`/${ROUTES.SOURCING}/${ROUTES.LISTS}/${item.id}`);
  const isSelected = match !== null;
  const { menuPos, handleMenuButtonClick, handleContextMenu, handleClose } = useContextMenu();
  const duplicateDialog = useDuplicateListDialog(item.id);
  // Destructure stable refs so the menu-builder useMemo doesn't recompute
  // every render off the hook's fresh return object.
  const { isPending: isDuplicatePending, openDialog: openDuplicateDialog } = duplicateDialog;
  const { isPending: isEnrichLoading, mutate: enrichList } = useEnrichListNow();
  const [isHovered, setIsHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== item.name) {
      onRename(item, trimmed);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitRename();
    }
    if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  const showActions = isHovered || isSelected || Boolean(menuPos);

  const openRefreshSettings = useSetAtom(refreshSettingsDialogAtom);

  const menuSections = useMemo(
    () =>
      buildListMenuSections({
        list: item,
        isDuplicating: isDuplicatePending,
        isEnrichLoading,
        isRunLoading: false,
        onRename: () => {
          setEditValue(item.name);
          setEditing(true);
        },
        onDuplicate: () => {
          handleClose();
          openDuplicateDialog();
        },
        onDelete: () => onDelete(item),
        onEnrichNow: () => enrichList(item),
        onRunNow: () => {},
        onOpenRefreshSettings: () => openRefreshSettings(item),
        onCopyLinkError: (message) => pushErrorToast({ message }),
      }),
    [
      item,
      onDelete,
      pushErrorToast,
      isDuplicatePending,
      openDuplicateDialog,
      isEnrichLoading,
      enrichList,
      handleClose,
      openRefreshSettings,
    ]
  );

  if (editing) {
    return (
      <NewItemInputWrapper>
        <StyledInputBase
          inputRef={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={handleKeyDown}
        />
      </NewItemInputWrapper>
    );
  }

  return (
    <>
      <ButtonBase
        TouchRippleProps={{ color: 'white' }}
        style={{ width: '100%', display: 'block' }}
        component={Link}
        to={`/${ROUTES.SOURCING}/${ROUTES.LISTS}/${item.id}`}
      >
        <ItemRow
          isSelected={isSelected}
          isHovered={isHovered}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onContextMenu={handleContextMenu}
        >
          <ItemInner>
            <ItemLeft>
              {item.entityType === 'person' ? (
                <ListPersonIcon color={secondary[20]} />
              ) : (
                <ListCompanyIcon color={secondary[20]} />
              )}
              <Typography
                variant='body2'
                color={secondary[10]}
                noWrap
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {item.name}
              </Typography>
            </ItemLeft>
            {showActions && (
              <IconButton
                size='small'
                onClick={handleMenuButtonClick}
                sx={{ color: secondary[10], p: '2px', flexShrink: 0 }}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </ItemInner>
        </ItemRow>
        <SourcingMenu sections={menuSections} menuPos={menuPos} onClose={handleClose} />
      </ButtonBase>
      <DuplicateListDialog
        open={duplicateDialog.open}
        loading={duplicateDialog.isPending}
        originalName={item.name}
        existingNames={duplicateDialog.existingNames}
        serverError={duplicateDialog.serverError}
        onCancel={duplicateDialog.closeDialog}
        onConfirm={duplicateDialog.confirm}
      />
    </>
  );
};
